import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { pdfjs } from 'react-pdf';
import '@/lib/pdfConfig'; // Ensure worker is configured

interface ThumbnailResult {
  success: boolean;
  thumbnailPath?: string;
  error?: string;
}

/**
 * Hook to generate PDF thumbnails client-side using react-pdf
 * This runs silently in the background and doesn't block the UI
 */
export function usePdfThumbnailGenerator() {
  
  const generatePdfThumbnail = useCallback(async (
    file: File,
    documentId: string,
    userId: string
  ): Promise<ThumbnailResult> => {
    let pdf: ReturnType<typeof pdfjs.getDocument> extends { promise: Promise<infer T> } ? T : never | null = null;
    
    try {
      console.log('[PDF Thumbnail] Starting generation for:', documentId);
      console.log('[PDF Thumbnail] File size:', file.size);
      console.log('[PDF Thumbnail] Worker configured:', !!pdfjs.GlobalWorkerOptions.workerSrc);
      
      // Read the file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load the PDF document using react-pdf's configured pdfjs instance
      const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
      pdf = await loadingTask.promise;
      
      console.log('[PDF Thumbnail] PDF loaded, pages:', pdf.numPages);
      
      // Get the first page
      const page = await pdf.getPage(1);
      
      // Calculate scale for a reasonable thumbnail size (max 400px width)
      const viewport = page.getViewport({ scale: 1 });
      const targetWidth = 400;
      const scale = targetWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });
      
      // Create an off-screen canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      if (!context) {
        throw new Error('Could not create canvas context');
      }
      
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      
      // Render the page to canvas
      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
        canvas: canvas,
      }).promise;
      
      console.log('[PDF Thumbnail] Canvas rendered');
      
      // Convert canvas to JPEG blob (quality 0.7 for reasonable size)
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.7);
      });
      
      if (!blob) {
        throw new Error('Failed to create thumbnail blob');
      }
      
      console.log('[PDF Thumbnail] Blob created, size:', blob.size);
      
      // Generate thumbnail path
      const thumbnailPath = `${userId}/${documentId}.jpg`;
      
      // Upload to thumbnails bucket
      const { error: uploadError } = await supabase.storage
        .from('thumbnails')
        .upload(thumbnailPath, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });
      
      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      console.log('[PDF Thumbnail] Upload successful:', thumbnailPath);
      
      // Update document with thumbnail path
      const { error: updateError } = await supabase
        .from('documents')
        .update({ thumbnail_path: thumbnailPath })
        .eq('id', documentId);
      
      if (updateError) {
        console.warn('[PDF Thumbnail] Failed to update document:', updateError);
        // Don't throw - thumbnail is uploaded, just DB update failed
      }
      
      return { success: true, thumbnailPath };
      
    } catch (error) {
      console.error('[PDF Thumbnail] Generation failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    } finally {
      // Cleanup PDF resources
      if (pdf) {
        try { pdf.destroy(); } catch (e) { /* ignore cleanup errors */ }
      }
    }
  }, []);

  return { generatePdfThumbnail };
}
